import { css, useTheme } from '@emotion/react';
import cn from 'classnames';
import { noop } from 'lodash';
import { type HTMLProps, memo, type NamedExoticComponent, type ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useDelayedClosingState, useMemoizedBundle } from '@didaskill/ui/utils';

import { useDrawer } from '../../../utils/hooks/useDrawer';
import { MODAL_ROOT_ID } from '../../meta/dialogs-provider/constants';
import { Button } from '../button/Button';
import { LayerContext } from '../layer/Layer.context';
import { LayerOverlay } from '../layer-overlay/LayerOverlay';
import { DrawerActions } from './components/actions/DrawerActions';
import { DRAWER_POSITIONS, DRAWER_SIZES } from './constants';
import * as styles from './Drawer.styles';

export type DrawerProps = Omit<HTMLProps<HTMLDivElement>, 'ref' | 'size'> & {
  /** Is the layer open. */
  isOpen?: boolean;
  /** Width of the drawer. */
  size?: DRAWER_SIZES;
  /** Position of the drawer. */
  position?: DRAWER_POSITIONS;
  /** Should request to close the layer when pressing escape. */
  shouldCloseOnEscape?: boolean;
  /** Should close the modal when clicking on the overlay. */
  shouldCloseOnClickOverlay?: boolean;
  /** Root element className. */
  containerClassName?: string;
  /** Called when requesting to close the layer. */
  onRequestClose?: () => void;
  /** Called when the exit animation is done and the content is unmounted. */
  onDidClose?: () => void;
  /** Drawer content. */
  children?: ReactNode;
};

export const DrawerBase = ({
  isOpen = false,
  size = DRAWER_SIZES.MEDIUM,
  position = DRAWER_POSITIONS.RIGHT,
  shouldCloseOnEscape = true,
  shouldCloseOnClickOverlay = true,
  containerClassName = undefined,
  onRequestClose = noop,
  onDidClose = noop,
  children = undefined,
  ...props
}: DrawerProps) => {
  const theme = useTheme();
  const drawerRef = useRef();

  // Handle closing last open dialog on escape and background blur.
  const { numberOfLayersAbove, layerZIndex } = useDrawer({
    isOpen,
    shouldCloseOnEscape,
    onRequestClose,
  });

  const [isVisible, isClosing] = useDelayedClosingState({
    isOpen,
    onDidClose,
    animationDuration: theme.transitions.default.duration * 2,
  });

  const contextValue = useMemoizedBundle({
    isInLayer: true,
    layerZIndex,
    shouldCloseOnClickOverlay,
    onRequestClose,
  });

  return (
    <LayerContext.Provider value={contextValue}>
      <LayerOverlay
        isVisible={isVisible}
        isClosing={isClosing}
        hasLayersAbove={numberOfLayersAbove > 0}
        containerClassName={containerClassName}
      >
        <div
          ref={drawerRef}
          className={cn(size, position, {
            isOpen: isVisible && !isClosing,
            isClosing,
          })}
          css={styles.drawerStyle}
        >
          {isVisible && (
            <div
              {...props}
              css={css`
                border: none;
                width: 100%;
                height: 100%;
                background-color: ${theme.colors.gray[0]};
                overflow: auto;
              `}
            >
              {children}
            </div>
          )}
        </div>
      </LayerOverlay>
    </LayerContext.Provider>
  );
};

DrawerBase.displayName = 'Drawer';

export const Drawer: NamedExoticComponent<DrawerProps> & {
  SIZES?: typeof DRAWER_SIZES;
  POSITIONS?: typeof DRAWER_POSITIONS;
  Actions?: typeof DrawerActions;
  Action?: typeof Button;
} = memo((props) =>
  createPortal(<DrawerBase {...props} />, document.getElementById(MODAL_ROOT_ID)),
);

Drawer.SIZES = DRAWER_SIZES;
Drawer.POSITIONS = DRAWER_POSITIONS;
Drawer.Actions = DrawerActions;
Drawer.Action = Button;
